import { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { NavLink, useNavigate } from "react-router";

import Button from "../common/Button";
import Badge from "../common/Badge";
import Page from "../common/Page";
import Card from "../common/Card";
import Table from "../common/Table";
import Loading from "../common/Loading";

import { useTranslation, Translation } from "react-i18next";
import { capitalizeFirst } from "../../utils/manipulateText";
import LanguageSelector from "../common/LanguageSelector";
import MessageText from "../common/MessageText";
import APIManager from "../../managers/APIManager";

const PaymentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userId = useSelector((state) => state.auth?.user?.id);
  const integrationType = useSelector(
    (state) => state.auth?.user?.integrationType
  );

  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    if (integrationType === 1) {
      navigate("/analytics/videos");
      return;
    }

    Promise.all([
      APIManager.getPayments(userId),
      APIManager.getPaymentInfo(userId),
    ])
      .then(([paymentsResponse, paymentInfoResponse]) => {
        setPaymentData([...paymentsResponse.data.payments]);
        setPaymentInfo({ ...paymentInfoResponse.data });
      })
      .catch((error) => {
        console.error("An error occurred:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userId, navigate, integrationType]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Page
      title={
        <>
          <div className={"flex justify-between items-center my-1"}>
            <h2 className="text-xl font-semibold text-gray-700 tracking-tight leading-tight">
              <Translation>{(t) => capitalizeFirst(t("payments"))}</Translation>
            </h2>
            <LanguageSelector customStyle="ml-auto" />
          </div>
        </>
      }
      controls={
        <Button
          RootComponent={NavLink}
          to="/payments/edit"
          color="primary"
          disabled={false}
        >
          <Icon.CreditCard className="inline mr-2" width="16" height="16" />
          {t("update payment info")}
        </Button>
      }
    >
      {paymentData?.length > 0 ? (
        <Card className="w-2/3">
          <div className="overflow-x-scroll">
            <Table
              headers={[
                { content: "period", className: "text-left" },
                { content: "status", className: "text-left" },
                { content: "action date", className: "text-left" },
                { content: "this period", className: "text-left" },
                { content: "total", className: "text-left" },
                { content: "note", className: "text-left" },
              ]}
            >
              {paymentData?.map((payment) => (
                <Table.Row
                  key={payment.term}
                  dataKey={payment.termStr}
                  className={"even:bg-gray-100 hover:bg-gray-200"}
                >
                  <Table.Col>{payment.termStr}</Table.Col>
                  <Table.Col>
                    {payment.paid === 0 ? (
                      <Badge
                        key={payment.paid}
                        color="danger"
                        className="ml-1 inline-block"
                      >
                        unpaid
                      </Badge>
                    ) : (
                      [
                        payment.paid === 1 ? (
                          <Badge
                            key={payment.paid}
                            color="danger"
                            className="ml-1 inline-block"
                          >
                            unpaid
                          </Badge>
                        ) : (
                          [
                            payment.paid === 2 ? (
                              <Badge
                                key={payment.paid}
                                color="danger"
                                className="ml-1 inline-block"
                              >
                                unpaid
                              </Badge>
                            ) : (
                              <Badge
                                key={payment.paid}
                                color="success"
                                className="ml-1 inline-block"
                              >
                                paid
                              </Badge>
                            ),
                          ]
                        ),
                      ]
                    )}
                  </Table.Col>
                  <Table.Col>
                    {payment.paymentDate
                      ? moment(payment.paymentDate).format("ddd, MM/D/YYYY")
                      : "-"}
                  </Table.Col>
                  <Table.Col className="font-semibold">
                    ${payment.amount}
                  </Table.Col>
                  <Table.Col>${payment.total}</Table.Col>
                  <Table.Col className="text-xs">
                    {payment.paid === 0
                      ? capitalizeFirst(
                          t("earn x to get paid.", {
                            more: `$${250 - payment.total}`,
                          })
                        )
                      : ""}
                    {payment.note ? payment.note : ""}
                  </Table.Col>
                </Table.Row>
              ))}
            </Table>
          </div>

          <MessageText
            message={"you get paid when your total earnings reach to $250."}
            customClass={"p-4 text-blue-600 text-xs justify-center"}
            textAlignment="text-left"
          />
        </Card>
      ) : paymentInfo?.iban ? (
        <MessageText
          message={
            "Your payment information has been received. You can view the details on this page once your payment is processed."
          }
        />
      ) : (
        <MessageText
          message={
            "No payment information is attached to your account. Please update your payment information."
          }
        />
      )}
    </Page>
  );
};

export default PaymentList;
